import { FormComboBox } from '@/components/forms/form-combo-box.js';
import { FormInputDate } from '@/components/forms/form-input-date.js';
import { FormRadioGroup } from '@/components/forms/form-radio-group.js';
import { FormTextArea } from '@/components/forms/form-text-area.js';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.js';
import { Badge } from '@/components/ui/badge.js';
import { MultiSelectOption } from '@/components/ui/multi-select.js';
import { comboBoxOptionsOf, emptySelectOption, generateOptionLabel } from '@/components/ui/multi-select-utils.js';
import { coachesTypeaheadSearch, useAvailableCoachesSearch } from '@/lib/api-call-hooks.js';
import { getCoaches, refreshSystemSuggestedCoaches } from '@/lib/api-calls.js';
import { AllSelectTeacherTypes, AllUpdateTeacherTypeValues, CoachesTypeaheadSearchOptions } from '@/lib/coach-utils.js';
import {
  bulkReassignTeacherFormSchema,
  getPreferredTeacherUnavailableHint,
  getReassignmentAttentionNeededMessages,
  getUniqueSystemSuggestedCoachIds,
  reassignmentNotesMaxLength,
  shouldShowReassignmentNotes,
} from '@/lib/student-group-reassignment-utils.js';
import { cn } from '@/lib/tailwind-utils.js';
import {
  anyStudentGroupNeedsProtectedPeriodOverride,
  Final14DayProtectionNotice,
  ProtectedPeriodOverrideFields,
} from '@/routes/_layout/student-groups/-components/protected-period-override-fields.js';
import { distinct, formatPlainDate, isNotNullish } from '@airreading/common';
import {
  classEligibilitiesRequiredForLearningPathNames,
  CoachStatus,
  GetCoachesSortOrder,
  PendingStudentGroupReassignmentRequest,
  ScheduleFromStudentGroupBulkActions,
  StudentGroupWithScheduleAndLevel,
  UserLike,
} from '@airreading/common/data';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lightbulb } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

type BulkReassignTeacherFormValues = z.infer<typeof bulkReassignTeacherFormSchema>;

export interface BulkReassignTeacherFormProps {
  form: UseFormReturn<BulkReassignTeacherFormValues>;
  reassignmentRequests: PendingStudentGroupReassignmentRequest[];
  studentGroups: StudentGroupWithScheduleAndLevel[];
  effectivePlainDate: Temporal.PlainDate;
  endPlainDate: Temporal.PlainDate;
}

function displayUserLikeValue(users: (UserLike | null | undefined)[]): string {
  const labels = users.map((user) => user?.fullNameOrEmail ?? '-');
  const uniqueLabels = [...new Set(labels)];
  return uniqueLabels.length === 1 ? uniqueLabels[0]! : 'Multiple values';
}

function SystemSuggestedBadge() {
  return (
    <Badge
      variant="secondary"
      className="shrink-0 rounded-md border-transparent bg-air-blue-subdued px-2 py-0.5 text-xs font-medium text-air-blue hover:bg-air-blue-subdued"
    >
      System suggested
    </Badge>
  );
}

function withSystemSuggestedBadge(option: MultiSelectOption, isSystemSuggested: boolean): MultiSelectOption {
  if (!isSystemSuggested) {
    return option;
  }

  return {
    ...option,
    rightText: (
      <div className="flex items-center gap-1.5 shrink-0">
        <SystemSuggestedBadge />
        {option.rightText}
      </div>
    ),
  };
}

function renderSelectedTeacherOption(
  option: MultiSelectOption | undefined,
  systemSuggestedCoachIds: Set<string>,
): ReactNode {
  if (!option) {
    return 'Select a teacher';
  }

  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate">{option.label}</span>
      {systemSuggestedCoachIds.has(option.value) && <SystemSuggestedBadge />}
    </span>
  );
}

export const BulkReassignTeacherForm = ({
  form,
  reassignmentRequests,
  studentGroups,
  effectivePlainDate,
  endPlainDate,
}: BulkReassignTeacherFormProps) => {
  const queryClient = useQueryClient();
  const [selectedTeacherType, setSelectedTeacherType] = useState<string | undefined>(
    AllUpdateTeacherTypeValues.Available,
  );
  const [selectedUpdateType, setSelectedUpdateType] = useState<string | undefined>(
    ScheduleFromStudentGroupBulkActions.FromNow,
  );

  const { watch, setValue, getValues } = form;
  const updateType = watch('updateType');
  const overrideStartPlainDate = watch('startPlainDate');
  const overrideEndPlainDate = watch('endPlainDate');
  const coachAvailable = watch('coachAvailable');
  const coachAll = watch('coachAll');
  const reassignmentNotes = watch('reassignmentNotes');

  const reassignmentRequestIds = useMemo(
    () => reassignmentRequests.map((request) => request.id),
    [reassignmentRequests],
  );

  /**
   * Suggestions are stored when a request is created or edited, so they are stale by the time an
   * admin opens this modal if the suggested teacher has since been assigned elsewhere. This
   * mounts with the modal, so the recalculation only runs for the requests being acted on.
   */
  const { data: refreshedSuggestions, isPending } = useQuery({
    ...refreshSystemSuggestedCoaches(reassignmentRequestIds),
    enabled: reassignmentRequestIds.length > 0,
    gcTime: 0,
  });

  const isRefreshingSuggestions = reassignmentRequestIds.length > 0 && isPending;
  const currentRequests = refreshedSuggestions?.reassignmentRequests ?? reassignmentRequests;

  const attentionNeededMessages = useMemo(
    () => getReassignmentAttentionNeededMessages(currentRequests),
    [currentRequests],
  );
  const preferredTeacherUnavailableHint = useMemo(
    () => getPreferredTeacherUnavailableHint(currentRequests),
    [currentRequests],
  );

  const selectedCoachId =
    selectedTeacherType === AllUpdateTeacherTypeValues.Available ? coachAvailable?.value : coachAll?.value;
  const showReassignmentNotes = shouldShowReassignmentNotes(currentRequests, selectedCoachId);
  const reassignmentNotesLength = reassignmentNotes?.length ?? 0;
  const isOverReassignmentNotesLimit = reassignmentNotesLength > reassignmentNotesMaxLength;

  useEffect(() => {
    setValue('requiresReassignmentNotes', showReassignmentNotes, { shouldDirty: false, shouldValidate: false });
    if (!showReassignmentNotes && reassignmentNotes) {
      setValue('reassignmentNotes', '', { shouldDirty: true, shouldValidate: true });
    }
  }, [showReassignmentNotes, reassignmentNotes, setValue]);

  useEffect(() => {
    if (updateType === ScheduleFromStudentGroupBulkActions.FromNow) {
      setValue('startPlainDate', effectivePlainDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('endPlainDate', endPlainDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else if (selectedUpdateType !== updateType) {
      setValue('startPlainDate', effectivePlainDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('endPlainDate', endPlainDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setSelectedUpdateType(updateType);
    }
  }, [updateType, setValue, selectedUpdateType, effectivePlainDate, endPlainDate]);

  useEffect(() => {
    const sub = watch((value, { name }) => {
      if (name === 'teacherType' && value.teacherType) {
        setValue('coachAvailable', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        setValue('coachAll', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        setSelectedTeacherType(value.teacherType);
        queryClient.invalidateQueries(getCoaches());
      }
    });
    return () => sub.unsubscribe();
  }, [setValue, watch, queryClient]);

  const groupClassTimes = studentGroups
    .flatMap((group) => group.classTime)
    .filter(distinct((classTime) => classTime.id));
  const hasValidAvailabilityDateRange = Temporal.PlainDate.compare(overrideStartPlainDate, overrideEndPlainDate) <= 0;
  const availabilityPlainDates = hasValidAvailabilityDateRange
    ? { overrideStartPlainDate, overrideEndPlainDate }
    : null;
  const classTimeIdsForAvailability = groupClassTimes.map((classTime) => classTime.id);
  const orgId = studentGroups[0]?.classTime?.schedule?.school.org.id || '';

  const classEligibilities = useMemo(
    () =>
      classEligibilitiesRequiredForLearningPathNames(
        studentGroups.map((group) => group.learningPath?.name).filter(isNotNullish),
      ),
    [studentGroups],
  );

  const coachesSearchOptions = {
    pageIndex: 0,
    // Same ranking as the system suggestion, applied before the 50-row page:
    // fewest active classes, then overall grade.
    sortBy: ['studentGroupCount-asc', 'overallGrade-asc'] satisfies GetCoachesSortOrder,
    filters: {
      availabilities: {
        classTimes: classTimeIdsForAvailability,
        orgId,
        classEligibilities,
        ...(availabilityPlainDates ?? {}),
      },
    },
  };

  const allCoachesSearchOptions: CoachesTypeaheadSearchOptions = {
    shouldDisplayOverallGrade: true,
    filters: {
      statuses: [CoachStatus.ready_to_teach],
    },
  };

  const { data, isLoading: isLoadingCoaches } = useAvailableCoachesSearch(coachesSearchOptions, {
    shouldDisplayOverallGrade: true,
    sortByAvailabilityPriority: true,
  });

  const originalCoachIds = useMemo(
    () =>
      new Set(reassignmentRequests.flatMap((request) => (request.originalCoach?.id ? [request.originalCoach.id] : []))),
    [reassignmentRequests],
  );

  const coachesFromSearch = useMemo(
    () => (data || []).filter((coach) => !originalCoachIds.has(coach.value)),
    [data, originalCoachIds],
  );

  /**
   * Only teachers the live availability search still returns can be badged. The search is ordered by
   * the same priority as the suggestion, so an eligible suggestion is on this page.
   */
  const systemSuggestedCoachIds = useMemo(() => {
    const availableCoachIds = new Set(coachesFromSearch.map((coach) => coach.value));
    return new Set(getUniqueSystemSuggestedCoachIds(currentRequests).filter((id) => availableCoachIds.has(id)));
  }, [coachesFromSearch, currentRequests]);

  const availableCoaches = useMemo(
    () => coachesFromSearch.map((coach) => withSystemSuggestedBadge(coach, systemSuggestedCoachIds.has(coach.value))),
    [coachesFromSearch, systemSuggestedCoachIds],
  );

  useEffect(() => {
    if (selectedTeacherType !== AllUpdateTeacherTypeValues.Available || isLoadingCoaches || isRefreshingSuggestions) {
      return;
    }

    const currentValue = getValues('coachAvailable')?.value;
    if (currentValue) {
      return;
    }

    const uniqueSuggestedIds = getUniqueSystemSuggestedCoachIds(currentRequests);
    const [suggestedCoachId] = uniqueSuggestedIds;
    if (uniqueSuggestedIds.length !== 1 || !suggestedCoachId || !systemSuggestedCoachIds.has(suggestedCoachId)) {
      return;
    }

    const suggestedOption = availableCoaches.find((coach) => coach.value === suggestedCoachId);
    if (suggestedOption) {
      setValue('coachAvailable', suggestedOption, { shouldDirty: true, shouldValidate: true });
    }
  }, [
    availableCoaches,
    currentRequests,
    getValues,
    isLoadingCoaches,
    isRefreshingSuggestions,
    selectedTeacherType,
    setValue,
    systemSuggestedCoachIds,
  ]);

  const allCoachesTypeaheadSearch = useMemo(() => {
    const search = coachesTypeaheadSearch(queryClient, allCoachesSearchOptions);
    return async (typeahead: string) => {
      const coaches = await search(typeahead);
      return coaches.filter((coach) => !originalCoachIds.has(coach.value));
    };
  }, [queryClient, allCoachesSearchOptions, originalCoachIds]);

  const teacherSelect =
    selectedTeacherType === AllUpdateTeacherTypeValues.Available ? (
      <div className="col-span-2 space-y-1">
        <FormComboBox
          form={form}
          label="Available teachers"
          name="coachAvailable"
          placeholder="Select teacher"
          {...comboBoxOptionsOf(availableCoaches)}
          shouldHideCheck={true}
          renderOption={(option) => generateOptionLabel(option)}
          renderOptionSelected={(option) => renderSelectedTeacherOption(option, systemSuggestedCoachIds)}
          emptySearchOptionMessage="No available teachers"
          isLoadingKnownOptions={isLoadingCoaches}
          triggerProps={{ className: 'w-full' }}
        />
        {preferredTeacherUnavailableHint && (
          <p className="text-sm text-muted-foreground">{preferredTeacherUnavailableHint}</p>
        )}
      </div>
    ) : (
      <div className="col-span-2">
        <FormComboBox
          form={form}
          label="All teachers"
          name="coachAll"
          placeholder="Select teacher"
          {...comboBoxOptionsOf(allCoachesTypeaheadSearch)}
          shouldHideCheck={true}
          renderOptionSelected={(option) => (option ? option.label : 'Select a teacher')}
          triggerProps={{ className: 'w-full' }}
        />
      </div>
    );

  const reassignmentEffectiveDateOptions = [
    {
      label: `Future sessions from effective date: ${formatPlainDate(effectivePlainDate)}`,
      value: ScheduleFromStudentGroupBulkActions.FromNow,
    },
    {
      label: 'Future sessions in range',
      value: ScheduleFromStudentGroupBulkActions.WithRange,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-3 pt-4">
      <div>
        <div className="text-sm font-bold">Current teacher</div>
        <div className="mt-1 text-sm">
          {displayUserLikeValue(reassignmentRequests.map((request) => request.originalCoach))}
        </div>
      </div>
      <div>
        <div className="text-sm font-bold">Preferred new teacher</div>
        <div className="mt-1 text-sm">
          {displayUserLikeValue(reassignmentRequests.map((request) => request.preferredCoach))}
        </div>
      </div>

      <div className="col-span-2">
        <FormRadioGroup label="Search teacher" form={form} name="teacherType" options={AllSelectTeacherTypes} />
      </div>
      {teacherSelect}

      {showReassignmentNotes && (
        <div className="col-span-2">
          <FormTextArea
            form={form}
            name="reassignmentNotes"
            label="Reassignment notes"
            labelEndContent={
              <span className={cn('text-sm text-muted-foreground', isOverReassignmentNotesLimit && 'text-destructive')}>
                {reassignmentNotesLength} / {reassignmentNotesMaxLength}
              </span>
            }
            textAreaProps={{
              className: 'min-h-[72px] resize-none',
              placeholder: 'Reason for not selecting system suggested teacher',
              required: true,
            }}
          />
        </div>
      )}

      {attentionNeededMessages.length > 0 && (
        <Alert
          variant="alert"
          className="col-span-2 gap-0 border-orange-300 bg-[#FFEDD5] px-4 py-3 text-orange-700 [&>svg]:left-4 [&>svg]:top-3.5 [&>svg]:text-orange-700 [&>svg~*]:pl-7"
        >
          <Lightbulb className="h-4 w-4" />
          <AlertTitle className="mb-1.5 text-sm font-semibold leading-none text-orange-700">
            Attention needed
          </AlertTitle>
          <AlertDescription className="space-y-1 text-sm text-orange-700">
            {attentionNeededMessages.map((message) => (
              <p key={message} className="leading-snug">
                {message}
              </p>
            ))}
          </AlertDescription>
        </Alert>
      )}

      <div className="col-span-2">
        <FormRadioGroup
          label="Reassignment effective date"
          form={form}
          name="updateType"
          options={reassignmentEffectiveDateOptions}
        />
      </div>
      {updateType === ScheduleFromStudentGroupBulkActions.WithRange && (
        <>
          <FormInputDate name="startPlainDate" label="Start date" form={form} />
          <FormInputDate name="endPlainDate" label="End date" form={form} />
        </>
      )}

      {anyStudentGroupNeedsProtectedPeriodOverride(studentGroups) && (
        <div className="col-span-2 space-y-3">
          {studentGroups.map((group) => (
            <Final14DayProtectionNotice
              key={group.id}
              attendancePolicyV2={group.attendancePolicyV2}
              final14DayProtection={group.final14DayProtection}
            />
          ))}
          <ProtectedPeriodOverrideFields form={form} required />
        </div>
      )}
    </div>
  );
};
